import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Loader2, ChevronRight } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { authApi, type OrgOption } from '@/api/auth'
import { applyAuthenticated } from '@/lib/slackAuth'

interface Payload {
  selection_token: string
  organizations: OrgOption[]
}

const ROLE_LABEL: Record<string, string> = {
  team_member: 'Member',
  project_lead: 'Project Lead',
  manager: 'Manager',
  admin: 'Admin',
  accounts: 'Accounts',
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || '')
    .join('')
}

export default function SelectOrg() {
  const navigate = useNavigate()
  const { setAuth } = useAuthStore()
  const [payload, setPayload] = useState<Payload | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const raw = sessionStorage.getItem('slack_org_select')
    if (!raw) {
      navigate('/login', { replace: true })
      return
    }
    try {
      setPayload(JSON.parse(raw))
    } catch {
      navigate('/login', { replace: true })
    }
  }, [navigate])

  const choose = async (org: OrgOption) => {
    if (!payload || busy) return
    setBusy(org.org_slug)
    setError(null)
    try {
      const result = await authApi.slackSelectOrg(payload.selection_token, org.org_slug)
      sessionStorage.removeItem('slack_org_select')
      applyAuthenticated(result, setAuth, navigate)
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Could not sign in to that organization'
      setError(detail)
      setBusy(null)
    }
  }

  if (!payload) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-[#EC5B2C]" />
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <div className="max-w-md w-full space-y-6">
        <div className="text-center">
          <img src="/logo-dark.png" alt="Managition" className="h-9 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900">Choose your organization</h1>
          <p className="mt-1 text-sm text-gray-500">
            Your Slack account has access to more than one workspace.
          </p>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 text-center">{error}</div>
        )}

        <div className="space-y-3">
          {payload.organizations.map((org) => (
            <button
              key={org.org_slug}
              type="button"
              disabled={busy !== null}
              onClick={() => choose(org)}
              className="w-full flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3 text-left shadow-sm transition hover:border-[#EC5B2C] hover:shadow disabled:opacity-60"
            >
              {org.icon_url ? (
                <img
                  src={org.icon_url}
                  alt=""
                  className="h-10 w-10 rounded-lg object-cover bg-gray-100"
                />
              ) : (
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#EC5B2C]/10 text-sm font-semibold text-[#EC5B2C]">
                  {initials(org.org_name)}
                </span>
              )}
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium text-gray-900">{org.org_name}</span>
                <span className="block text-xs text-gray-500">
                  {ROLE_LABEL[org.role] || org.role}
                </span>
              </span>
              {busy === org.org_slug ? (
                <Loader2 className="h-4 w-4 animate-spin text-[#EC5B2C]" />
              ) : (
                <ChevronRight className="h-4 w-4 text-gray-300" />
              )}
            </button>
          ))}
        </div>

        <p className="text-center text-sm text-gray-400">
          <a href="/login" className="hover:underline">
            Use a different account
          </a>
        </p>
      </div>
    </div>
  )
}
